import { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { Icon } from '../../icon'
import { Z_INDEX } from '../tabs'

const SCROLL_OFFSET = 250

const StyledScrollButtons = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    position: sticky;
    right: 0;
    border-left: 1px solid ${theme.colors.gray90};
    padding: ${theme.sizes.xs};
    background: ${theme.colors.white};
    z-index: ${Z_INDEX.OVERLAY};
  `}
`

const StyledScrollButton = styled.button`
  ${({ theme }) => css`
    padding: 0;
    margin: 0;
    border: none;
    background: none;
    cursor: pointer;
    padding: 2px;

    &:hover {
      color: ${theme.colors.primary400};
    }

    &:disabled {
      color: ${theme.colors.gray100};
    }
  `}
`

export const ScrollButtons = ({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLDivElement>
}) => {
  const [scrollLeft, setScrollLeft] = useState(
    containerRef.current?.scrollLeft ?? 0
  )

  const handleScroll = (direction: 'left' | 'right') => {
    const offset = direction === 'left' ? -SCROLL_OFFSET : SCROLL_OFFSET

    containerRef.current?.scrollTo({
      left: (containerRef.current?.scrollLeft ?? 0) + offset,
      behavior: 'smooth',
    })
  }

  const handleScrollEvent = useCallback(() => {
    setScrollLeft(containerRef.current?.scrollLeft ?? 0)
  }, [containerRef])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScrollEvent)
    return () => container.removeEventListener('scroll', handleScrollEvent)
  }, [containerRef, handleScrollEvent])

  const [isScrollLeftDisabled, isScrollRightDisabled] = useMemo(() => {
    return [
      scrollLeft === 0,
      scrollLeft ===
        (containerRef.current?.scrollWidth ?? 0) -
          (containerRef.current?.clientWidth ?? 0),
    ]
  }, [scrollLeft, containerRef])

  // Shouldn't happen as we render the buttons only when there is overflow, but just in case
  if (isScrollLeftDisabled && isScrollRightDisabled) {
    return null
  }

  return (
    <StyledScrollButtons data-testid="scroll-tabs-buttons">
      <StyledScrollButton
        onClick={() => handleScroll('left')}
        data-testid="scroll-tabs-left-button"
        disabled={isScrollLeftDisabled}
      >
        <Icon name="chevron-left" size="s" />
      </StyledScrollButton>
      <StyledScrollButton
        onClick={() => handleScroll('right')}
        data-testid="scroll-tabs-right-button"
        disabled={isScrollRightDisabled}
      >
        <Icon name="chevron-right" size="s" />
      </StyledScrollButton>
    </StyledScrollButtons>
  )
}
